/**
 * A base address and how to sign in to it, shared by every tool that calls the same system.
 *
 * THE SECRET GOES IN AND NEVER COMES BACK. The dashboard is told whether one is set and its
 * last four characters; leaving the box empty on an edit keeps the stored one, and "Remove the
 * secret" is the only way to clear it. The Worker encrypts it before it is stored and decrypts
 * it only inside the request that uses it.
 */

import { useState } from "react";
import { ApiError, post, type Connection } from "../../api";
import { Dialog } from "../Dialog";
import { Field } from "../Form";
import { Alert } from "../Icons";
import { Button, Select, TextInput } from "../../ui";

type Auth = Connection["auth"];

export function ConnectionDialog({
  agentId,
  connection,
  onClose,
  onSaved,
}: {
  agentId: string;
  connection: Connection | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(connection?.name ?? "");
  const [baseUrl, setBaseUrl] = useState(connection?.baseUrl ?? "");
  const [auth, setAuth] = useState<Auth>(connection?.auth ?? "bearer");
  const [headerName, setHeaderName] = useState(connection?.headerName ?? "");
  const [username, setUsername] = useState(connection?.username ?? "");
  const [secret, setSecret] = useState("");
  const [clearSecret, setClearSecret] = useState(false);
  const [busy, setBusy] = useState<"save" | "delete" | "test" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tested, setTested] = useState<string | null>(null);

  async function save() {
    setBusy("save");
    setError(null);
    const body: Record<string, unknown> = { name, baseUrl, auth, headerName, username };
    // Omitted keeps the stored secret; "" clears it. See this file's header.
    if (clearSecret) body.secret = "";
    else if (secret) body.secret = secret;
    try {
      await post(connection ? `/v1/agent/connections/${encodeURIComponent(connection.id)}` : "/v1/agent/connections", {
        agent: agentId,
        connection: body,
      });
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "The dashboard could not reach the server.");
    } finally {
      setBusy(null);
    }
  }

  async function test() {
    if (!connection) return;
    setBusy("test");
    setTested(null);
    try {
      const answer = await post<{ ok: boolean; status: number | null; durationMs: number; error?: string }>(
        `/v1/agent/connections/${encodeURIComponent(connection.id)}/test`,
        { agent: agentId },
      );
      setTested(answer.ok ? `It answered ${answer.status} in ${answer.durationMs} ms.` : (answer.error ?? "It did not answer."));
    } catch (err) {
      setTested(err instanceof ApiError ? err.message : "The dashboard could not reach the server.");
    } finally {
      setBusy(null);
    }
  }

  async function remove() {
    if (!connection) return;
    setBusy("delete");
    try {
      await post(`/v1/agent/connections/${encodeURIComponent(connection.id)}/delete`, { agent: agentId });
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "The dashboard could not reach the server.");
      setBusy(null);
    }
  }

  return (
    <Dialog
      title={connection ? "Edit connection" : "New connection"}
      onClose={onClose}
      footer={
        <>
          <Button type="button" variant="primary" disabled={busy !== null} onClick={() => void save()}>
            {busy === "save" ? "Saving…" : connection ? "Save" : "Create"}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          {connection && (
            <Button type="button" variant="ghost" className="ml-auto" disabled={busy !== null} onClick={() => void test()}>
              {busy === "test" ? "Testing…" : "Test connection"}
            </Button>
          )}
        </>
      }
    >
      <Field label="Name">
        <TextInput autoFocus className="w-full" value={name} placeholder="e.g. Shopify" onChange={(event) => setName(event.target.value)} />
      </Field>
      <Field label="Base address" hint="A tool's path is added to the end of this.">
        <TextInput className="w-full font-mono" value={baseUrl} placeholder="https://api.example.com/v1" onChange={(event) => setBaseUrl(event.target.value)} />
      </Field>
      <Field label="Sign in with">
        <Select className="w-full" value={auth} onChange={(event) => setAuth(event.target.value as Auth)}>
          <option value="bearer">Bearer token</option>
          <option value="header">API key in a header</option>
          <option value="basic">User name and password</option>
          <option value="none">Nothing</option>
        </Select>
      </Field>
      {auth === "header" && (
        <Field label="Header name">
          <TextInput className="w-full font-mono" value={headerName} placeholder="X-Api-Key" onChange={(event) => setHeaderName(event.target.value)} />
        </Field>
      )}
      {auth === "basic" && (
        <Field label="User name">
          <TextInput className="w-full" value={username} onChange={(event) => setUsername(event.target.value)} />
        </Field>
      )}
      {auth !== "none" && (
        <Field
          label={auth === "basic" ? "Password" : "Secret"}
          hint={
            connection?.secretSet && !clearSecret
              ? `Stored, ending ${connection.secretLast4 || "····"}. Leave empty to keep it.`
              : "Stored encrypted. Never shown again."
          }
        >
          <TextInput
            type="password"
            autoComplete="off"
            className="w-full font-mono"
            value={secret}
            disabled={clearSecret}
            onChange={(event) => setSecret(event.target.value)}
          />
        </Field>
      )}
      {connection?.secretSet && auth !== "none" && (
        <label className="flex items-center gap-2 text-sm text-ink-soft">
          <input type="checkbox" checked={clearSecret} onChange={(event) => setClearSecret(event.target.checked)} />
          Remove the stored secret
        </label>
      )}

      {tested && <p className="text-sm text-ink-soft">{tested}</p>}
      {error && (
        <p role="alert" className="flex gap-2 rounded-sm border border-alert-line bg-alert-soft px-3 py-2.5 text-sm text-ink">
          <Alert size={16} className="mt-px flex-none text-alert" />
          <span className="text-ink-soft">{error}</span>
        </p>
      )}

      {connection && (
        <div className="border-t border-line-soft pt-3">
          {connection.usedBy > 0 ? (
            <p className="text-sm text-ink-faint">
              Used by {connection.usedBy} {connection.usedBy === 1 ? "tool" : "tools"}. Point them elsewhere to delete it.
            </p>
          ) : (
            <Button type="button" variant="ghost" className="text-alert" disabled={busy !== null} onClick={() => void remove()}>
              {busy === "delete" ? "Deleting…" : "Delete connection"}
            </Button>
          )}
        </div>
      )}
    </Dialog>
  );
}
